import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/** Dạng tối thiểu dùng chung cho ảnh đã lưu (bug, nghiệm thu, tính năng). */
export interface SavedImage {
  id: string;
  caption?: string | null;
  file_url?: string | null;
}

interface SavedImageGridProps<T extends SavedImage> {
  images: T[];
  /** Dựng URL tuyệt đối cho thẻ <img> — mỗi loại ảnh có endpoint raw riêng. */
  resolveUrl: (image: T) => string;
  /** Bỏ qua để chỉ xem (không hiện nút xoá). */
  onRemove?: (image: T) => void;
  size?: number;
}

/**
 * Lưới ảnh ĐÃ LƯU trên server, click để phóng to. Tách khỏi ImageUploader
 * (chỉ lo ảnh chưa gửi) vì hai thứ có vòng đời khác hẳn nhau: ảnh ở đây xoá là
 * gọi API, còn ảnh bên kia chỉ nằm trong bộ nhớ trình duyệt.
 */
export function SavedImageGrid<T extends SavedImage>({
  images,
  resolveUrl,
  onRemove,
  size = 96,
}: SavedImageGridProps<T>): JSX.Element | null {
  const [viewing, setViewing] = useState<T | null>(null);

  if (images.length === 0) return null;

  return (
    <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-testid="saved-image-grid">
        {images.map((img) => (
          <Box
            key={img.id}
            sx={{
              position: 'relative',
              width: size,
              height: size,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              component="img"
              src={resolveUrl(img)}
              alt={img.caption ?? 'Ảnh đính kèm'}
              onClick={() => setViewing(img)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
            />
            {onRemove && (
              <IconButton
                aria-label="Xoá ảnh"
                size="small"
                onClick={() => onRemove(img)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                }}
              >
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            )}
          </Box>
        ))}
      </Stack>

      <Dialog open={viewing !== null} onClose={() => setViewing(null)} maxWidth="lg">
        <IconButton
          aria-label="Đóng"
          onClick={() => setViewing(null)}
          sx={{ position: 'absolute', right: 8, top: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
        >
          <CloseIcon />
        </IconButton>
        {viewing && (
          <Box
            component="img"
            src={resolveUrl(viewing)}
            alt={viewing.caption ?? 'Ảnh đính kèm'}
            sx={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh' }}
          />
        )}
      </Dialog>
    </>
  );
}
